import { all, call, put, takeLatest } from 'redux-saga/effects';
import { api } from '../../configurations/api';
import {
    userBaseUrl,
    usersAdminAdvancedSearchFilterOptionsUrl,
    usersAdminAdvancedSearchUrl,
} from '../../configurations/api/url';
import { SentryCapture } from '../../analytics/Sentry';
import { httpRequest } from '../types';
import {
    compactAdvancedSearchPayload,
    normalizeAdvancedSearchResult,
    normalizeFilterOptions,
} from '../../utils/adminUserAdvancedSearch';
import { parseUserSearchResponse } from '../../utils/adminUserSearch';
import {
    adminSearchUsers,
    adminSearchUsersFail,
    adminSearchUsersSuccess,
    fetchAdminAdvancedSearchFilterOptions,
    fetchAdminAdvancedSearchFilterOptionsFail,
    fetchAdminAdvancedSearchFilterOptionsSuccess,
    postAdminAdvancedSearch,
    postAdminAdvancedSearchFail,
    postAdminAdvancedSearchSuccess,
    type IFetchAdminAdvancedSearchFilterOptionsPayload,
} from './actions';
import type { IAdminUserAdvancedSearchPayload } from '../../types/global/adminUserAdvancedSearch.types';

function settle(
    resolve: ((v: any) => void) | undefined,
    reject: ((e: any) => void) | undefined,
    value: any,
    isError = false,
) {
    if (isError) {
        if (typeof reject === 'function') reject(value);
        return;
    }
    if (typeof resolve === 'function') resolve(value);
}

function* adminSearchUsersSaga({
    payload,
    resolve,
    reject,
}: {
    payload: string;
    resolve?: (v: any) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    try {
        const term = String(payload || '').trim();
        if (term.length < 3) {
            yield put(adminSearchUsersSuccess([]));
            settle(resolve, reject, []);
            return;
        }
        const query = new URLSearchParams({
            withProfileSearch: 'true',
            withSelf: 'true',
        });
        const url = `${userBaseUrl}/search?${query.toString()}`;
        const response = yield call(
            api,
            url,
            httpRequest.POST,
            { searchTerm: term, withProfileSearch: true, withSelf: true },
            2,
        );
        const users = parseUserSearchResponse(response);
        yield put(adminSearchUsersSuccess(users));
        settle(resolve, reject, users);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(adminSearchUsersFail(error));
        settle(resolve, reject, error, true);
    }
}

function* fetchFilterOptionsSaga({
    payload,
    resolve,
    reject,
}: {
    payload?: IFetchAdminAdvancedSearchFilterOptionsPayload;
    resolve?: (v: any) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    try {
        const q = new URLSearchParams();
        if (payload?.facet) q.set('facet', payload.facet);
        const facetSearch = (payload?.search ?? payload?.searchTerm)?.trim();
        if (facetSearch) q.set('search', facetSearch);
        if (payload?.page) q.set('page', String(payload.page));
        if (payload?.limit) q.set('limit', String(payload.limit));
        const qs = q.toString();
        const url = qs ? `${usersAdminAdvancedSearchFilterOptionsUrl}?${qs}` : usersAdminAdvancedSearchFilterOptionsUrl;
        const response = yield call(api, url, httpRequest.GET, null, 2);
        const data = normalizeFilterOptions(response);
        yield put(fetchAdminAdvancedSearchFilterOptionsSuccess(data));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(fetchAdminAdvancedSearchFilterOptionsFail(error));
        settle(resolve, reject, error, true);
    }
}

function* postAdvancedSearchSaga({
    payload,
    resolve,
    reject,
}: {
    payload: IAdminUserAdvancedSearchPayload;
    resolve?: (v: any) => void;
    reject?: (e: any) => void;
}): Generator<any, void, any> {
    try {
        const body = compactAdvancedSearchPayload(payload);
        const response = yield call(api, usersAdminAdvancedSearchUrl, httpRequest.POST, body, 2);
        const data = normalizeAdvancedSearchResult(response);
        yield put(postAdminAdvancedSearchSuccess(data));
        settle(resolve, reject, data);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(postAdminAdvancedSearchFail(error));
        settle(resolve, reject, error, true);
    }
}

export default function* adminUserSearchSaga() {
    yield all([
        takeLatest(adminSearchUsers as any, adminSearchUsersSaga),
        takeLatest(fetchAdminAdvancedSearchFilterOptions as any, fetchFilterOptionsSaga),
        takeLatest(postAdminAdvancedSearch as any, postAdvancedSearchSaga),
    ]);
}
